import React, { useContext, useEffect, useState } from 'react';
import { Card, Grid, Icon, Label, SemanticICONS } from 'semantic-ui-react';
import { AppContext } from '../../contexts/appContext';
import { ContactContext } from '../../contexts/contactContext';
import { Logger, LoggingService } from '../../services/LoggingService';
import { ISierraViewConfig, ISierraViewField } from '../../models/serviceConfigs';

const logger: Logger = new LoggingService().getLogger('SierraView');

const getAttrValue = (attributes: connect.AttributeDictionary, key: string): string =>
  attributes[key]?.value ?? '';

const formatDateTime = (value: string): string => {
  if (!value) return 'N/A';
  try {
    const date = new Date(value);
    if (isNaN(date.getTime())) return value;
    return date.toLocaleString(undefined, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return value;
  }
};


const AgeDisplay = ({ value }: { value: boolean | string }) => {
  const isTrue =
    value === true ||
    value?.toString().toLowerCase() === 'true';

  return (
    <Label color={isTrue ? 'green' : 'red'} horizontal>
      <Icon name={isTrue ? 'check circle' : 'times circle'} />
      {isTrue ? 'Yes' : 'NO'}
    </Label>
  );
};

const BooleanDisplay = ({ value }: { value: string }) => {
  const isValid = value?.toLowerCase() === 'true' || value?.toLowerCase() === 'yes';

  return (
    <Label color={isValid ? 'green' : 'red'} horizontal>
      <Icon name={isValid ? 'check circle' : 'times circle'} />
      {isValid ? 'Valid' : 'Invalid'}
    </Label>
  );
};

const PaymentDisplay = ({ value }: { value: string }) => {
  if (!value) return <span style={{ color: '#555' }}>N/A</span>;

  const lower = value.toLowerCase();
  const isDebit = lower.includes('debit');
  const isCredit = lower.includes('credit');

  return (
    <Label color={isDebit ? 'teal' : isCredit ? 'blue' : 'grey'} horizontal>
      <Icon name="credit card" />
      {value}
    </Label>
  );
};

const renderFieldValue = (field: ISierraViewField, value: string): JSX.Element => {
  switch (field.displayType) {
    case 'dateTime':
      return <p>{formatDateTime(value)}</p>;
    case 'age':
      return <AgeDisplay value={value} />;
    case 'boolean':
      return <BooleanDisplay value={value} />;
    case 'payment':
      return <PaymentDisplay value={value} />;
    default:
      return <p style={{ color: '#333' }}>{value || 'N/A'}</p>;
  }
};

export const SierraView = () => {
  const { config } = useContext(AppContext);
  const { selectedContact } = useContext(ContactContext);
  const [contactAttributes, setContactAttributes] = useState<connect.AttributeDictionary>(() => {
    try {
      return selectedContact?.getAttributes() ?? {};
    } catch {
      return {};
    }
  });

  useEffect(() => {
    if (!selectedContact) return;
    try {
      setContactAttributes(selectedContact.getAttributes());
    } catch {
      logger.debug('Contact no longer exists, attributes not refreshed');
    }
  }, [selectedContact]);

  if (!selectedContact) {
    return <div title="MissingSierraView" />;
  }

  const service = contactAttributes?.service?.value;
  const sierraConfig: ISierraViewConfig | undefined = service
    ? config.getServiceConfig(service)?.sierraView
    : undefined;

  if (!sierraConfig) {
    return <div title="MissingSierraView" />;
  }

  const paragraphFields = sierraConfig.fields.filter(
    f => f.displayType === 'paragraph' && (!f.hideIfBlank || !!getAttrValue(contactAttributes, f.attributeKey))
  );

  const gridFields = sierraConfig.fields.filter(
    f => f.displayType !== 'paragraph' && (!f.hideIfBlank || !!getAttrValue(contactAttributes, f.attributeKey))
  );

  // Group into pairs for the 2-column grid
  const fieldPairs: ISierraViewField[][] = [];
  for (let i = 0; i < gridFields.length; i += 2) {
    fieldPairs.push(gridFields.slice(i, i + 2));
  }

  return (
    <Card title="SierraView" fluid className="card-jmb">
      <Card.Content className="header ean">
        <Card.Header textAlign="left">
          <Icon name="car" />
          Rental Details
        </Card.Header>
      </Card.Content>
      {paragraphFields.map(field => (
        <Card.Content key={field.attributeKey}>
          <p style={{ marginBottom: '4px' }}>
            <strong>
              {field.icon && <Icon name={field.icon as SemanticICONS} />}
              {field.label}
            </strong>
          </p>
          <p style={{ color: '#333' }}>{getAttrValue(contactAttributes, field.attributeKey)}</p>
        </Card.Content>
      ))}
      <Card.Content>
        <Grid columns={2} relaxed="very">
          {fieldPairs.map((pair, i) => (
            <Grid.Row key={i}>
              {pair.map(field => (
                <Grid.Column key={field.attributeKey}>
                  <p style={{ marginBottom: '6px' }}>
                    <strong>
                      {field.icon && <Icon name={field.icon as SemanticICONS} />}
                      {field.label}
                    </strong>
                  </p>
                  {renderFieldValue(field, getAttrValue(contactAttributes, field.attributeKey))}
                </Grid.Column>
              ))}
            </Grid.Row>
          ))}
        </Grid>
      </Card.Content>
    </Card>
  );
};
